import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import {
  Container,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Grid,
  Paper,
  Stack,
  CircularProgress,
  Box,
} from "@mui/material";

const DiabetesPatientGroomingForm = ({ patientName }) => {
  const { register, handleSubmit, reset, setValue } = useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [groomingData, setGroomingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Fix: Add missing state variables
  const [usingCGM, setUsingCGM] = useState(""); // Track CGM selection
  const [cgmDataUploaded, setCgmDataUploaded] = useState(false); // Track if CGM data is available
  const [cgmAnalysis, setCgmAnalysis] = useState(null); // Store AI analysis
  const [uploadedFiles, setUploadedFiles] = useState([]); // ✅ Store uploaded files

  // Add state for analysis status
  const [analysisInProgress, setAnalysisInProgress] = useState(false);

  console.log("passed patient name:", patientName);

  // ✅ Handle Multiple File Uploads
  const handleFileUpload = (event) => {
    setUploadedFiles([...event.target.files]); // Store multiple files
  };

  // ✅ Process CGM Data with Backend
  const processCGMData = async () => {
    try {
      setAnalysisInProgress(true);

      if (!uploadedFiles?.length) {
        alert("Please upload files first");
        return;
      }

      const formData = new FormData();
      uploadedFiles.forEach((file) => formData.append("files", file));
      formData.append("patientName", encodeURIComponent(patientName));
      formData.append(
        "cgmManufacturer",
        usingCGM.includes("Dexcom") ? "Dexcom" : "Libre"
      );

      const response = await axios.post(
        "https://app.metabolytics.ai/simulator-backend/patients/api/process-cgm",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.analysis) {
        setCgmDataUploaded(true);
        setCgmAnalysis(response.data.analysis);

        // Only update values if analysis data exists
        if (response.data.analysis.time_in_range) {
          setValue(
            "glucose_monitoring.glucose_trends.time_in_range",
            `${response.data.analysis.time_in_range}%`
          );
          setValue(
            "glucose_monitoring.glucose_trends.hypoglycemia_episodes",
            `${response.data.analysis.hypoglycemia_episodes} episodes in last month`
          );
          setValue(
            "glucose_monitoring.glucose_trends.variability",
            response.data.analysis.variability
          );
        }
      }
    } catch (error) {
      console.error("❌ Error processing CGM data:", error);
      alert("Failed to process CGM data");
    } finally {
      setAnalysisInProgress(false);
    }
  };

  // Update the form to auto-populate glucose trends when CGM data is processed:
  useEffect(() => {
    if (cgmAnalysis) {
      setValue(
        "glucose_monitoring.glucose_trends.time_in_range",
        `${cgmAnalysis.time_in_range}%`
      );
      setValue(
        "glucose_monitoring.glucose_trends.hypoglycemia_episodes",
        `${cgmAnalysis.hypoglycemia_episodes} episodes in last month`
      );
      setValue(
        "glucose_monitoring.glucose_trends.variability",
        cgmAnalysis.variability < 25
          ? "Stable"
          : cgmAnalysis.variability < 35
          ? "Moderate Variability"
          : "High Variability"
      );
    }
  }, [cgmAnalysis, setValue]);

  useEffect(() => {
    if (!patientName) {
      console.error(
        "❌ Error: `patientName` is undefined. Cannot fetch grooming data."
      );
      setLoading(false);
      return;
    }
    fetchGroomingData();
  }, [patientName]);

  // ✅ Fetch existing grooming data (if available)
  const fetchGroomingData = async () => {
    try {
      const response = await axios.get(
        `https://app.metabolytics.ai/simulator-backend/patients/api/grooming?name=${encodeURIComponent(
          patientName
        )}`
      );
      if (response.data) {
        setGroomingData(response.data);
        setUsingCGM(response.data?.glucose_monitoring?.using_cgm || "");
        Object.keys(response.data).forEach((key) => {
          setValue(key, response.data[key]);
        });
        setIsEditing(true);

        // Check for CGM data
        if (response.data?.glucose_monitoring?.using_cgm !== "No") {
          try {
            const cgmCheck = await axios.get(
              `https://app.metabolytics.ai/simulator-backend/patients/api/cgmdatas/${encodeURIComponent(
                patientName
              )}`
            );
            if (cgmCheck.data?.message === "CGM data available") {
              setCgmDataUploaded(true);
              setCgmAnalysis(cgmCheck.data.analysis);
            }
          } catch (cgmError) {
            console.warn("No CGM data available yet");
          }
        }
      }
    } catch (error) {
      console.warn(
        "⚠️ No existing grooming data found. Generating new data..."
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      console.log("Submitting Grooming Data:", data);

      if (isEditing) {
        await axios.put(
          `https://app.metabolytics.ai/simulator-backend/patients/api/grooming?name=${encodeURIComponent(
            patientName
          )}`,
          data
        );
        alert("Grooming data updated successfully!");
      } else {
        await axios.post(
          "https://app.metabolytics.ai/simulator-backend/patients/api/grooming",
          { patient_name: patientName, ...data }
        );
        alert("Grooming data saved successfully!");
      }
      reset();
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      alert("Failed to submit form. Please try again.");
    }
  };

  if (!patientName) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
          <Typography variant="h6" align="center" color="error">
            ❌ Error: Patient Name is missing. Unable to load form.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Diabetes Patient Grooming Form
        </Typography>

        {loading ? (
          <Stack alignItems="center">
            <CircularProgress />
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Loading grooming data...
            </Typography>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Patient Demographics */}
              <Typography variant="h6">Patient Demographics</Typography>
              <TextField
                label="Full Name"
                {...register("full_name")}
                defaultValue={groomingData?.full_name}
                required
                fullWidth
              />
              <TextField
                label="Phone"
                {...register("contact_info.phone")}
                defaultValue={groomingData?.contact_info?.phone}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                {...register("contact_info.email")}
                defaultValue={groomingData?.contact_info?.email}
                required
                fullWidth
              />
              <TextField
                label="Address"
                {...register("contact_info.address")}
                defaultValue={groomingData?.contact_info?.address}
                required
                fullWidth
              />

              {/* Insurance Details */}
              <Typography variant="h6">Insurance Details</Typography>
              <TextField
                label="Insurance Provider"
                {...register("insurance_details.provider")}
                defaultValue={groomingData?.insurance_details?.provider}
                required
                fullWidth
              />
              <TextField
                label="Policy Number"
                {...register("insurance_details.policy_number")}
                defaultValue={groomingData?.insurance_details?.policy_number}
                required
                fullWidth
              />

              {/* Medical History */}
              <Typography variant="h6">Medical History</Typography>
              <FormControl fullWidth>
                <InputLabel>Type of Diabetes</InputLabel>
                <Select
                  {...register("medical_history.type_of_diabetes")}
                  defaultValue={groomingData?.medical_history?.type_of_diabetes}
                  required
                >
                  <MenuItem value="Type 1 Diabetes">Type 1 Diabetes</MenuItem>
                  <MenuItem value="Type 2 Diabetes">Type 2 Diabetes</MenuItem>
                  <MenuItem value="Gestational Diabetes">
                    Gestational Diabetes
                  </MenuItem>
                  <MenuItem value="Prediabetes">Prediabetes</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Duration of Diabetes (years)"
                type="number"
                {...register("medical_history.duration_years")}
                defaultValue={groomingData?.medical_history?.duration_years}
                required
                fullWidth
              />
              <TextField
                label="Comorbidities"
                multiline
                rows={2}
                {...register("medical_history.comorbidities")}
                defaultValue={groomingData?.medical_history?.comorbidities}
                fullWidth
              />
              <TextField
                label="Recent Health Changes or Hospitalizations"
                multiline
                rows={2}
                {...register("medical_history.recent_health_changes")}
                defaultValue={
                  groomingData?.medical_history?.recent_health_changes
                }
                fullWidth
              />

              {/* ✅ Medications */}
              <Typography variant="h6">Medications</Typography>

              {/* ✅ Current Diabetes Medications */}
              <TextField
                label="Current Diabetes Medications"
                multiline
                rows={2}
                value={
                  groomingData?.medications?.diabetes_medications
                    ?.map(
                      (med) => `${med.name} - ${med.dose}, ${med.frequency}`
                    )
                    .join("\n") || "N/A"
                }
                fullWidth
              />

              {/* ✅ Adherence Issues */}
              <TextField
                label="Adherence Issues"
                value={groomingData?.medications?.adherence_issues || "N/A"}
                fullWidth
              />

              {/* ✅ Non-Diabetes Medications */}
              <TextField
                label="Non-Diabetes Medications"
                multiline
                rows={2}
                value={
                  groomingData?.medications?.non_diabetes_medications
                    ?.map(
                      (med) => `${med.name} - ${med.dose}, ${med.frequency}`
                    )
                    .join("\n") || "N/A"
                }
                fullWidth
              />

              {/* ✅ Allergies */}
              <TextField
                label="Allergies to Medications"
                multiline
                rows={2}
                value={
                  groomingData?.medications?.allergies?.join(", ") || "N/A"
                }
                fullWidth
              />

              {/* ✅ Lab Results */}
              <Typography variant="h6">Lab Results</Typography>

              <TextField
                label="Latest HbA1c (%)"
                {...register("lab_results.latest_hba1c.value")}
                defaultValue={groomingData?.lab_results?.latest_hba1c}
                fullWidth
              />
              <TextField
                label="Recent Fasting Blood Glucose"
                {...register("lab_results.fasting_blood_glucose")}
                defaultValue={groomingData?.lab_results?.fasting_blood_glucose}
                fullWidth
              />
              <TextField
                label="Kidney Function - eGFR"
                {...register("lab_results.kidney_function.eGFR")}
                defaultValue={groomingData?.lab_results?.kidney_function?.eGFR}
                fullWidth
              />
              <TextField
                label="Kidney Function - Urine Albumin/Creatinine Ratio"
                {...register(
                  "lab_results.kidney_function.urine_albumin_creatinine_ratio"
                )}
                defaultValue={
                  groomingData?.lab_results?.kidney_function
                    ?.urine_albumin_creatinine_ratio
                }
                fullWidth
              />
              <TextField
                label="Lipid Panel - LDL"
                {...register("lab_results.lipid_panel.LDL")}
                defaultValue={groomingData?.lab_results?.lipid_panel?.LDL}
                fullWidth
              />
              <TextField
                label="Lipid Panel - HDL"
                {...register("lab_results.lipid_panel.HDL")}
                defaultValue={groomingData?.lab_results?.lipid_panel?.HDL}
                fullWidth
              />
              <TextField
                label="Lipid Panel - Triglycerides"
                {...register("lab_results.lipid_panel.triglycerides")}
                defaultValue={
                  groomingData?.lab_results?.lipid_panel?.triglycerides
                }
                fullWidth
              />

              {/* ✅ Glucose Monitoring */}
              <Typography variant="h6">Glucose Monitoring</Typography>

              {/* ✅ Is the Patient Using a CGM? */}
              <FormControl fullWidth>
                <InputLabel>Is the Patient Using a CGM?</InputLabel>
                <Select
                  {...register("glucose_monitoring.using_cgm")}
                  value={usingCGM} // ✅ Fix: Use state variable
                  onChange={(e) => setUsingCGM(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="Yes - Dexcom">Yes - Dexcom</MenuItem>
                  <MenuItem value="Yes - Libre">Yes - Libre</MenuItem>
                  <MenuItem value="Yes - Dexcom/Libre">
                    Yes - Dexcom/Libre
                  </MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>

              {/* ✅ Check if CGM Data is Available */}
              {usingCGM !== "No" && (
                <>
                  {cgmDataUploaded ? (
                    <Box mt={2} p={2} border={1} borderRadius={2}>
                      <Typography variant="subtitle1">
                        ✅ CGM Analysis Results
                      </Typography>
                      {analysisInProgress ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <Typography>
                            CGM monitoring started: {cgmAnalysis?.start_date}
                          </Typography>
                          <Typography>
                            CGM monitoring finished: {cgmAnalysis?.end_date}
                          </Typography>
                          <Typography>
                            Number of Days: {cgmAnalysis?.total_days}
                          </Typography>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Box mt={2} p={2} border={1} borderRadius={2}>
                      <Typography variant="subtitle1">
                        ⚠️ No CGM Data Found (Last 30 Days)
                      </Typography>
                      <Typography>
                        Please upload Dexcom or Libre data files.
                      </Typography>

                      {/* ✅ Enable Multiple File Uploads */}
                      <input
                        type="file"
                        accept=".csv"
                        multiple
                        onChange={handleFileUpload}
                      />

                      {/* ✅ Button to Process Data After Upload */}
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={processCGMData}
                        disabled={uploadedFiles.length === 0}
                      >
                        Process CGM Data
                      </Button>
                    </Box>
                  )}
                </>
              )}

              {/* ✅ D{isp}lay Basic Glucose Trends */}
              <TextField
                label="Glucose Trends - Time in Range"
                {...register("glucose_monitoring.glucose_trends.time_in_range")}
                defaultValue={
                  groomingData?.glucose_monitoring?.glucose_trends
                    ?.time_in_range
                }
                fullWidth
              />
              <TextField
                label="Glucose Trends - Hypoglycemia Episodes"
                {...register(
                  "glucose_monitoring.glucose_trends.hypoglycemia_episodes"
                )}
                defaultValue={
                  groomingData?.glucose_monitoring?.glucose_trends
                    ?.hypoglycemia_episodes
                }
                fullWidth
              />
              <TextField
                label="Glucose Trends - Variability"
                {...register("glucose_monitoring.glucose_trends.variability")}
                defaultValue={
                  groomingData?.glucose_monitoring?.glucose_trends?.variability
                }
                fullWidth
              />
              <TextField
                label="Are Glucose Meter Logs Up to Date?"
                {...register(
                  "glucose_monitoring.glucose_meter_logs_up_to_date"
                )}
                defaultValue={
                  groomingData?.glucose_monitoring
                    ?.glucose_meter_logs_up_to_date
                }
                fullWidth
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                {isEditing ? "Update" : "Submit"}
              </Button>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export default DiabetesPatientGroomingForm;
